/**
 * SourceState - freshness metadata strip: source, observedAt, TTL,
 * completeness, confidence (Story #5351 W1-T4, Shared Contracts tab's
 * read_response envelope: data+scope+source+observedAt+freshness).
 */
import React from 'react';

interface SourceStateProps {
  source: string;
  observedAt: string | Date;
  /** Seconds until this data is considered stale. */
  ttlSeconds?: number;
  /** 0..1 — how much of the expected data is present. */
  completeness?: number;
  /** 0..1 — how much the source vouches for the value. */
  confidence?: number;
  className?: string;
}

function toDate(value: string | Date): Date {
  return typeof value === 'string' ? new Date(value) : value;
}

function formatRelative(value: string | Date): string {
  const seconds = Math.max(0, Math.floor((Date.now() - toDate(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

const SourceState: React.FC<SourceStateProps> = ({
  source, observedAt, ttlSeconds, completeness, confidence, className,
}) => (
  <dl className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 ${className ?? ''}`}>
    <div className="flex items-center gap-1">
      <dt className="font-medium text-slate-400">Source</dt>
      <dd>{source}</dd>
    </div>
    <div className="flex items-center gap-1">
      <dt className="font-medium text-slate-400">Observed</dt>
      <dd title={toDate(observedAt).toISOString()}>{formatRelative(observedAt)}</dd>
    </div>
    {ttlSeconds !== undefined && (
      <div className="flex items-center gap-1">
        <dt className="font-medium text-slate-400">TTL</dt>
        <dd>{ttlSeconds}s</dd>
      </div>
    )}
    {completeness !== undefined && (
      <div className="flex items-center gap-1">
        <dt className="font-medium text-slate-400">Completeness</dt>
        <dd>{formatPercent(completeness)}</dd>
      </div>
    )}
    {confidence !== undefined && (
      <div className="flex items-center gap-1">
        <dt className="font-medium text-slate-400">Confidence</dt>
        <dd>{formatPercent(confidence)}</dd>
      </div>
    )}
  </dl>
);

export default SourceState;
